import React, { useState, useMemo, useTransition } from "react";
import { format, parseISO } from "date-fns";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Clock,
  ChevronRight,
  GitBranch,
  CalendarIcon,
  X,
  ExternalLink,
  Info,
  Download,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/currency";
import { describeTermsCode, lookupTermsCode } from "@/lib/par/termsCodeMap";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Input } from "@/components/ui/input";
import type { PARInvoiceRow } from "@/lib/par/csvInvoiceParser";
import { exportPARVendorAnalysisCsv } from "@/lib/csvExport";

import { VendorFilterDropdown } from "@/components/par/VendorFilterDropdown";
import {
  NumericColumnHeader,
  TextColumnHeader,
  type ColumnSort,
} from "@/components/par/SortableColumnHeaders";

interface InvoiceDetailTableProps {
  invoices: PARInvoiceRow[];
  referenceDate?: string;
  onVendorDrillDown?: (vendor: string) => void;
  /** Cross-nav: called when user clicks "Negotiate" on a vendor row.
   *  Only rendered when provided (i.e. vendor_negotiator flag is on). */
  onNegotiate?: (vendor: string) => void;
  loading?: boolean;
}

const fmtDate = (d: string | undefined | null): string => {
  if (!d) return "—";
  try {
    return format(parseISO(d), "MMM d, yyyy");
  } catch {
    return d;
  }
};

/* ─── Sort types ─── */
type SortField =
  | "vendor"
  | "invoices"
  | "totalInvoiced"
  | "discountEligible"
  | "discountEligiblePct"
  | "invoicedTerms"
  | "daysEarlyLate"
  | "captured"
  | "discountInvoices"
  | "potentialOpportunity"
  | "simulation";

export function InvoiceDetailTable({
  invoices,
  onVendorDrillDown,
  onNegotiate,
  loading,
}: InvoiceDetailTableProps) {
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const [, startTransition] = useTransition();
  const [earlyPayVendorsOnly, setEarlyPayVendorsOnly] = useState(false);
  const [mixedTermsOnly, setMixedTermsOnly] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState<Set<string>>(new Set());
  const [columnSort, setColumnSort] = useState<ColumnSort<SortField> | null>(null);

  // Pre-filter to 2025/2026
  const yearFiltered = useMemo(
    () =>
      invoices.filter((r) => {
        if (!r.invoiced_date) return false;
        const year = new Date(r.invoiced_date).getFullYear();
        return year === 2025 || year === 2026;
      }),
    [invoices]
  );

  // Identify vendors that have ANY early pay invoices
  const vendorsWithAnyEarlyPay = useMemo(() => {
    const s = new Set<string>();
    for (const r of yearFiltered) {
      if (r.discount_pct != null && r.discount_pct > 0) s.add(r.vendor_name);
    }
    return s;
  }, [yearFiltered]);

  // Base filtered data (without vendor selection) for dynamic filter options
  const baseFiltered = useMemo(() => {
    let result = yearFiltered;
    if (fromDate) {
      const from = format(fromDate, "yyyy-MM-dd");
      result = result.filter((r) => r.invoiced_date && r.invoiced_date >= from);
    }
    if (toDate) {
      const to = format(toDate, "yyyy-MM-dd");
      result = result.filter((r) => r.invoiced_date && r.invoiced_date <= to);
    }
    if (earlyPayVendorsOnly) {
      result = result.filter((r) => vendorsWithAnyEarlyPay.has(r.vendor_name));
    }
    if (mixedTermsOnly) {
      const vendorHasDiscount = new Set<string>();
      const vendorHasNoDiscount = new Set<string>();
      for (const r of result) {
        if (r.discount_pct != null && r.discount_pct > 0) vendorHasDiscount.add(r.vendor_name);
        else vendorHasNoDiscount.add(r.vendor_name);
      }
      const mixedVendors = new Set(
        [...vendorHasDiscount].filter((v) => vendorHasNoDiscount.has(v))
      );
      result = result.filter((r) => mixedVendors.has(r.vendor_name));
    }
    return result;
  }, [yearFiltered, fromDate, toDate, earlyPayVendorsOnly, mixedTermsOnly, vendorsWithAnyEarlyPay]);

  const uniqueVendors = useMemo(() => {
    return [...new Set(baseFiltered.map((r) => r.vendor_name))].sort();
  }, [baseFiltered]);

  const filtered = useMemo(() => {
    let result = baseFiltered;
    if (selectedVendors.size > 0) {
      result = result.filter((r) => selectedVendors.has(r.vendor_name));
    }
    return result;
  }, [baseFiltered, selectedVendors]);

  if (loading && invoices.length === 0) {
    return (
      <div className="space-y-4">
        <div className="h-10 rounded-lg bg-muted/50 animate-pulse" />
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-12 rounded-lg bg-muted/40 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters row - unified */}
      <Card className="px-3 py-2.5 space-y-2">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-[10px] text-muted-foreground uppercase tracking-wide font-semibold">
            Filters
          </span>

          {/* Date range */}
          <div className="flex items-center gap-1.5">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "h-7 min-w-0 px-3 text-[11px] gap-1.5 justify-start",
                    fromDate && "border-primary/40 text-primary"
                  )}
                >
                  <CalendarIcon className="w-3 h-3" />
                  {fromDate ? format(fromDate, "MMM d, yyyy") : "From date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={fromDate}
                  onSelect={(d) => startTransition(() => setFromDate(d))}
                  disabled={(date) => (toDate ? date > toDate : false)}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
            <span className="text-[10px] text-muted-foreground">–</span>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    "h-7 min-w-0 px-3 text-[11px] gap-1.5 justify-start",
                    toDate && "border-primary/40 text-primary"
                  )}
                >
                  <CalendarIcon className="w-3 h-3" />
                  {toDate ? format(toDate, "MMM d, yyyy") : "To date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={toDate}
                  onSelect={(d) => startTransition(() => setToDate(d))}
                  disabled={(date) => (fromDate ? date < fromDate : false)}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
            {(fromDate || toDate) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() =>
                  startTransition(() => {
                    setFromDate(undefined);
                    setToDate(undefined);
                  })
                }
              >
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>

          <div className="w-px h-5 bg-border" />

          {/* Toggle filters */}
          <button
            onClick={() => setEarlyPayVendorsOnly(!earlyPayVendorsOnly)}
            className={cn(
              "inline-flex items-center gap-1.5 h-7 min-w-0 px-3 justify-center rounded-md text-[11px] font-medium border transition-colors",
              earlyPayVendorsOnly
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background text-muted-foreground border-input hover:bg-accent hover:text-accent-foreground"
            )}
          >
            <Clock className="w-3 h-3" />
            Early Pay Vendors
          </button>
          <button
            onClick={() => setMixedTermsOnly(!mixedTermsOnly)}
            className={cn(
              "inline-flex items-center gap-1.5 h-7 min-w-0 px-3 justify-center rounded-md text-[11px] font-medium border transition-colors",
              mixedTermsOnly
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background text-muted-foreground border-input hover:bg-accent hover:text-accent-foreground"
            )}
          >
            <GitBranch className="w-3 h-3" />
            Mixed Terms
          </button>
          {uniqueVendors.length > 1 && (
            <VendorFilterDropdown
              vendors={uniqueVendors}
              selected={selectedVendors}
              onChange={(next) => {
                setSelectedVendors(next);
              }}
              displayedCount={new Set(filtered.map((r) => r.vendor_name)).size}
            />
          )}
          {columnSort?.field === "invoicedTerms" &&
            columnSort.textFilter &&
            columnSort.textFilter.size > 0 && (
              <button
                onClick={() => setColumnSort(null)}
                className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[11px] font-medium border bg-primary/10 text-primary border-primary/30 hover:bg-primary/20 transition-colors"
                title="Clear terms filter"
              >
                {[...columnSort.textFilter].join(", ")}
                <X className="w-3 h-3" />
              </button>
            )}
        </div>
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs gap-1.5"
            onClick={() => exportPARVendorAnalysisCsv(filtered)}
            disabled={filtered.length === 0}
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </Button>
        </div>
      </Card>

      {/* Grouped by vendor table */}
      <GroupedVendorTable
        filtered={filtered}
        columnSort={columnSort}
        onColumnSort={setColumnSort}
        onVendorDrillDown={onVendorDrillDown}
        onNegotiate={onNegotiate}
      />
    </div>
  );
}

/* ─── Grouped vendor table ─── */

interface VendorGroup {
  vendor_name: string;
  vendor_number: string;
  invoices: PARInvoiceRow[];
  totalInvoiced: number;
  discountEligible: number; // $ amount of discount eligible savings
  discountEligiblePct: number; // % of total invoiced
  capturedSavings: number;
  potentialOpportunity: number; // $ if ALL invoices used the vendor's best discount_pct
  invoiceCount: number;
  discountCount: number;
  avgDaysEarlyLate: number;
  termsUsed: { term: string; count: number }[];
  // computed on the fly based on simRate
  simulationSavings?: number;
}

function computeDaysEarlyLate(r: PARInvoiceRow): number | null {
  let days: number | null = null;
  if (r.days_paid_early && r.days_paid_early !== 0) {
    days = r.days_paid_early;
  } else if (r.paid_date && r.due_date) {
    const paid = new Date(r.paid_date).getTime();
    const due = new Date(r.due_date).getTime();
    days = Math.round((due - paid) / (1000 * 60 * 60 * 24));
  }
  // Treat outliers (>100 days early or late) as invalid data
  if (days != null && Math.abs(days) > 100) return null;
  return days;
}

function formatDaysEarlyLate(days: number | null): string {
  if (days == null) return "—";
  if (days > 0) return `${days}d early`;
  if (days < 0) return `${Math.abs(days)}d late`;
  return "On time";
}

function daysEarlyLateColor(days: number | null): string {
  if (days == null) return "text-muted-foreground";
  if (days > 0) return "text-success";
  if (days < 0) return "text-destructive";
  return "text-muted-foreground";
}

/* ─── Invoice-level sort helper ─── */
function getInvoiceSortVal(r: PARInvoiceRow, field: SortField, simRate = 0): number | string {
  switch (field) {
    case "totalInvoiced":
      return r.amount_invoiced;
    case "discountEligible":
      return r.potential_savings;
    case "discountEligiblePct":
      return r.discount_pct ?? 0;
    case "daysEarlyLate":
      return computeDaysEarlyLate(r) ?? 0;
    case "captured":
      return r.paid_date && r.early_pay_due_date && r.paid_date <= r.early_pay_due_date
        ? r.potential_savings
        : 0;
    case "vendor":
      return r.invoice_number || r.description || "";
    case "invoicedTerms":
      return r.terms || "";
    case "simulation":
      return simRate > 0 ? (r.amount_invoiced * simRate) / 100 : 0;
    default:
      return 0;
  }
}

function GroupedVendorTable({
  filtered,
  columnSort,
  onColumnSort,
  onVendorDrillDown,
  onNegotiate,
}: {
  filtered: PARInvoiceRow[];
  columnSort: ColumnSort<SortField> | null;
  onColumnSort: (s: ColumnSort<SortField> | null) => void;
  onVendorDrillDown?: (vendor: string) => void;
  onNegotiate?: (vendor: string) => void;
}) {
  const [expandedVendors, setExpandedVendors] = useState<Set<string>>(new Set());
  const [simRateInput, setSimRateInput] = useState<string>("");
  const simRate = parseFloat(simRateInput.replace(/[^0-9.]/g, "")) || 0;

  const groups = useMemo(() => {
    const map = new Map<string, PARInvoiceRow[]>();
    for (const r of filtered) {
      const key = r.vendor_name || r.vendor_number;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(r);
    }
    const result: VendorGroup[] = [];
    for (const [, invoices] of map) {
      const termsCounts = new Map<string, number>();
      for (const i of invoices) {
        if (i.terms) termsCounts.set(i.terms, (termsCounts.get(i.terms) || 0) + 1);
      }
      const dc = invoices.filter((i) => i.discount_pct != null && i.discount_pct > 0).length;
      const totalInvoiced = invoices.reduce((s, i) => s + i.amount_invoiced, 0);
      const discountEligible = invoices.reduce((s, i) => s + i.potential_savings, 0);
      const discountEligiblePct = totalInvoiced > 0 ? (discountEligible / totalInvoiced) * 100 : 0;
      const maxDiscountPct = Math.max(...invoices.map((i) => i.discount_pct ?? 0));
      const potentialOpportunity = maxDiscountPct > 0 ? (totalInvoiced * maxDiscountPct) / 100 : 0;

      const daysValues = invoices.map(computeDaysEarlyLate).filter((d) => d != null) as number[];
      const avgDays =
        daysValues.length > 0 ? daysValues.reduce((s, d) => s + d, 0) / daysValues.length : 0;

      result.push({
        vendor_name: invoices[0].vendor_name,
        vendor_number: invoices[0].vendor_number,
        invoices,
        totalInvoiced,
        discountEligible,
        discountEligiblePct,
        capturedSavings: invoices
          .filter((i) => i.paid_date && i.early_pay_due_date && i.paid_date <= i.early_pay_due_date)
          .reduce((s, i) => s + i.potential_savings, 0),
        potentialOpportunity,
        invoiceCount: invoices.length,
        discountCount: dc,
        avgDaysEarlyLate: avgDays,
        termsUsed: Array.from(termsCounts.entries()).map(([term, count]) => ({ term, count })),
      });
    }
    return result;
  }, [filtered]);

  // Sort invoices within each group based on active column sort
  const sortInvoices = useMemo(() => {
    if (!columnSort?.dir)
      return (invoices: PARInvoiceRow[]) =>
        [...invoices].sort((a, b) => (a.invoiced_date || "").localeCompare(b.invoiced_date || ""));
    const dir = columnSort.dir === "asc" ? 1 : -1;
    const field = columnSort.field;
    return (invoices: PARInvoiceRow[]) => {
      return [...invoices].sort((a, b) => {
        const aVal = getInvoiceSortVal(a, field, simRate);
        const bVal = getInvoiceSortVal(b, field, simRate);
        if (typeof aVal === "string" && typeof bVal === "string")
          return dir * aVal.localeCompare(bVal);
        return dir * ((aVal as number) - (bVal as number));
      });
    };
  }, [columnSort, simRate]);

  // Apply column sorting and filtering
  const sortedGroups = useMemo(() => {
    let result = [...groups].map((g) => ({
      ...g,
      simulationSavings: simRate > 0 ? (g.totalInvoiced * simRate) / 100 : 0,
    }));

    if (columnSort?.textFilter && columnSort.textFilter.size > 0) {
      if (columnSort.field === "vendor") {
        result = result.filter((g) => columnSort.textFilter!.has(g.vendor_name));
      } else if (columnSort.field === "invoicedTerms") {
        // Filter vendor groups that have the selected terms, AND restrict their invoices
        result = result
          .filter((g) =>
            g.termsUsed.some((t) => columnSort.textFilter!.has(describeTermsCode(t.term)))
          )
          .map((g) => {
            // Filter invoices to only those matching the selected terms
            const matchingInvoices = g.invoices.filter((inv) =>
              inv.terms ? columnSort.textFilter!.has(describeTermsCode(inv.terms)) : false
            );
            return { ...g, invoices: matchingInvoices, invoiceCount: matchingInvoices.length };
          });
      }
    }

    if (columnSort?.numFilter) {
      const { min, max } = columnSort.numFilter;
      const getVal = (g: VendorGroup & { simulationSavings?: number }): number => {
        switch (columnSort.field) {
          case "invoices":
            return g.invoiceCount;
          case "totalInvoiced":
            return g.totalInvoiced;
          case "discountEligible":
            return g.discountEligible;
          case "discountEligiblePct":
            return g.discountEligiblePct;
          case "daysEarlyLate":
            return g.avgDaysEarlyLate;
          case "captured":
            return g.capturedSavings;
          case "discountInvoices":
            return g.discountCount;
          case "potentialOpportunity":
            return g.potentialOpportunity;
          case "simulation":
            return g.simulationSavings ?? 0;
          default:
            return 0;
        }
      };
      if (min != null) result = result.filter((g) => getVal(g) >= min!);
      if (max != null) result = result.filter((g) => getVal(g) <= max!);
    }

    if (columnSort?.dir) {
      const dir = columnSort.dir === "asc" ? 1 : -1;
      result.sort((a, b) => {
        switch (columnSort.field) {
          case "vendor":
            return dir * a.vendor_name.localeCompare(b.vendor_name);
          case "invoices":
            return dir * (a.invoiceCount - b.invoiceCount);
          case "totalInvoiced":
            return dir * (a.totalInvoiced - b.totalInvoiced);
          case "discountEligible":
            return dir * (a.discountEligible - b.discountEligible);
          case "discountEligiblePct":
            return dir * (a.discountEligiblePct - b.discountEligiblePct);
          case "daysEarlyLate":
            return dir * (a.avgDaysEarlyLate - b.avgDaysEarlyLate);
          case "captured":
            return dir * (a.capturedSavings - b.capturedSavings);
          case "discountInvoices":
            return dir * (a.discountCount - b.discountCount);
          case "potentialOpportunity":
            return dir * (a.potentialOpportunity - b.potentialOpportunity);
          case "simulation":
            return dir * ((a.simulationSavings ?? 0) - (b.simulationSavings ?? 0));
          case "invoicedTerms":
            return (
              dir *
              a.termsUsed
                .map((t) => t.term)
                .join(",")
                .localeCompare(b.termsUsed.map((t) => t.term).join(","))
            );
          default:
            return 0;
        }
      });
    } else if (!columnSort) {
      result.sort((a, b) => {
        const aU = a.discountEligible - a.capturedSavings;
        const bU = b.discountEligible - b.capturedSavings;
        if (bU !== aU) return bU - aU;
        return b.discountCount - a.discountCount;
      });
    }

    return result;
  }, [groups, columnSort, simRate]);

  const toggle = (vendor: string) => {
    const next = new Set(expandedVendors);
    if (next.has(vendor)) next.delete(vendor);
    else next.add(vendor);
    setExpandedVendors(next);
  };

  const allVendorNames = useMemo(() => groups.map((g) => g.vendor_name), [groups]);
  const allInvoicedTermsDescs = useMemo(() => {
    const s = new Set<string>();
    groups.forEach((g) => g.termsUsed.forEach((t) => s.add(describeTermsCode(t.term))));
    return [...s];
  }, [groups]);
  const colCount = 12;

  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto overflow-y-auto max-h-[60vh]">
        <Table className="table-fixed min-w-[900px]">
          <colgroup>
            <col style={{ width: "28px" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "76px" }} />
            <col style={{ width: "84px" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "58px" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "8%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "10%" }} />
          </colgroup>
          <TableHeader className="sticky top-0 z-10">
            <TableRow className="bg-background hover:bg-background">
              <TableHead className="text-xs font-semibold"></TableHead>
              <TextColumnHeader<SortField>
                label="Vendor"
                field="vendor"
                sort={columnSort}
                onSort={onColumnSort}
                allValues={allVendorNames}
                align="left"
              />
              <NumericColumnHeader<SortField>
                label="Invoices"
                field="invoices"
                sort={columnSort}
                onSort={onColumnSort}
              />
              <NumericColumnHeader<SortField>
                label="Disc. Invoices"
                field="discountInvoices"
                sort={columnSort}
                onSort={onColumnSort}
              />
              <NumericColumnHeader<SortField>
                label="Total Invoiced"
                field="totalInvoiced"
                sort={columnSort}
                onSort={onColumnSort}
              />
              <NumericColumnHeader<SortField>
                label="Disc. Eligible"
                field="discountEligible"
                sort={columnSort}
                onSort={onColumnSort}
              />
              <NumericColumnHeader<SortField>
                label="%"
                field="discountEligiblePct"
                sort={columnSort}
                onSort={onColumnSort}
              />
              <TextColumnHeader<SortField>
                label="Invoiced Terms"
                field="invoicedTerms"
                sort={columnSort}
                onSort={onColumnSort}
                allValues={allInvoicedTermsDescs}
                align="left"
              />
              <NumericColumnHeader<SortField>
                label="Days Early/Late"
                field="daysEarlyLate"
                sort={columnSort}
                onSort={onColumnSort}
              />
              <NumericColumnHeader<SortField>
                label="Captured"
                field="captured"
                sort={columnSort}
                onSort={onColumnSort}
              />
              <NumericColumnHeader<SortField>
                label="Opportunity"
                field="potentialOpportunity"
                sort={columnSort}
                onSort={onColumnSort}
                tooltip="Already-negotiated early pay discount terms that aren't being captured — based on actual invoice terms, not simulated."
              />
              <TableHead className="text-xs font-semibold text-right">
                <div className="space-y-1">
                  <div className="inline-flex items-center gap-1 text-muted-foreground">
                    <span>Simulation</span>
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-3 h-3 text-muted-foreground/60 cursor-help shrink-0" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[220px] text-xs">
                          Enter a custom discount rate (%) to simulate what savings would have been
                          on each vendor's total invoiced amount.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                  <div className="relative w-20 ml-auto">
                    <Input
                      value={simRateInput}
                      onChange={(e) => setSimRateInput(e.target.value)}
                      placeholder="e.g. 1.5"
                      className="h-6 text-xs pr-5 py-0 text-right"
                    />
                    <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground pointer-events-none">
                      %
                    </span>
                  </div>
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedGroups.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={colCount}
                  className="text-center text-xs text-muted-foreground py-8"
                >
                  No invoices match your filters
                </TableCell>
              </TableRow>
            ) : (
              sortedGroups.map((g) => {
                const isExpanded = expandedVendors.has(g.vendor_name);
                const sortedInvs = sortInvoices(g.invoices);
                return (
                  <React.Fragment key={g.vendor_name}>
                    {/* Vendor summary row */}
                    <TableRow
                      className={cn(
                        "cursor-pointer hover:bg-muted/60 border-l-3",
                        g.discountEligible > 0
                          ? "border-l-primary/60"
                          : "border-l-muted-foreground/30"
                      )}
                      onClick={() => toggle(g.vendor_name)}
                    >
                      <TableCell className="w-8 px-2">
                        <ChevronRight
                          className={cn(
                            "w-3.5 h-3.5 text-muted-foreground transition-transform",
                            isExpanded && "rotate-90"
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 group/vendor">
                          <div>
                            <p className="text-xs font-semibold text-foreground">{g.vendor_name}</p>
                            {g.vendor_number && (
                              <p className="text-[10px] text-muted-foreground font-mono">
                                {g.vendor_number}
                              </p>
                            )}
                          </div>
                          {onVendorDrillDown && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onVendorDrillDown(g.vendor_name);
                              }}
                              title="Open vendor drill-down"
                              className="transition-opacity ml-1 text-muted-foreground hover:text-primary"
                            >
                              <ExternalLink className="w-3 h-3" />
                            </button>
                          )}
                          {onNegotiate && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onNegotiate(g.vendor_name);
                              }}
                              title={`Negotiate early-payment terms with ${g.vendor_name}`}
                              className="transition-opacity ml-1 text-muted-foreground hover:text-primary text-[10px] font-medium"
                            >
                              Negotiate
                            </button>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-xs font-medium">{g.invoiceCount}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span
                          className={cn(
                            "text-xs font-medium",
                            g.discountCount > 0 ? "text-primary" : "text-muted-foreground"
                          )}
                        >
                          {g.discountCount > 0 ? g.discountCount : "—"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-xs font-bold">{formatCurrency(g.totalInvoiced)}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span
                          className={cn(
                            "text-xs font-semibold",
                            g.discountEligible > 0 ? "text-primary" : "text-muted-foreground"
                          )}
                        >
                          {g.discountEligible > 0 ? formatCurrency(g.discountEligible) : "—"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span
                          className={cn(
                            "text-xs font-semibold",
                            g.discountEligiblePct > 0 ? "text-primary" : "text-muted-foreground"
                          )}
                        >
                          {g.discountEligiblePct > 0
                            ? g.discountEligiblePct < 0.5
                              ? "<0.5%"
                              : `${g.discountEligiblePct.toFixed(1)}%`
                            : "—"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {g.termsUsed.length > 0 ? (
                            [...g.termsUsed]
                              .sort((a, b) => {
                                const aDiscount =
                                  lookupTermsCode(a.term)?.discountPct != null ? 0 : 1;
                                const bDiscount =
                                  lookupTermsCode(b.term)?.discountPct != null ? 0 : 1;
                                if (aDiscount !== bDiscount) return aDiscount - bDiscount;
                                return describeTermsCode(a.term).localeCompare(
                                  describeTermsCode(b.term)
                                );
                              })
                              .map((t, i) => {
                                const hasDiscount = lookupTermsCode(t.term)?.discountPct != null;
                                const describedTerm = describeTermsCode(t.term);
                                const isActiveFilter =
                                  columnSort?.field === "invoicedTerms" &&
                                  columnSort.textFilter?.size === 1 &&
                                  columnSort.textFilter.has(describedTerm);
                                return (
                                  <span
                                    key={i}
                                    role="button"
                                    title={
                                      isActiveFilter
                                        ? "Click to clear term filter"
                                        : "Click to filter by this term"
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (isActiveFilter) {
                                        onColumnSort(null);
                                      } else {
                                        onColumnSort({
                                          field: "invoicedTerms",
                                          dir: null,
                                          textFilter: new Set([describedTerm]),
                                        });
                                      }
                                    }}
                                    className={cn(
                                      "inline-block px-1.5 py-0.5 rounded text-[9px] font-medium border",
                                      "cursor-pointer transition-all duration-150",
                                      isActiveFilter
                                        ? "bg-primary text-primary-foreground border-primary shadow-sm"
                                        : hasDiscount
                                          ? "bg-success/10 text-success border-success/20 hover:bg-success/20 hover:border-success/40"
                                          : "bg-muted text-muted-foreground border-border hover:bg-muted-foreground/15 hover:border-muted-foreground/30"
                                    )}
                                  >
                                    {describedTerm} ({t.count})
                                  </span>
                                );
                              })
                          ) : (
                            <span className="text-[10px] text-muted-foreground">—</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={cn("text-xs", daysEarlyLateColor(g.avgDaysEarlyLate))}>
                          {g.avgDaysEarlyLate !== 0
                            ? formatDaysEarlyLate(Math.round(g.avgDaysEarlyLate))
                            : "—"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div>
                          <span
                            className={cn(
                              "text-xs font-semibold",
                              g.capturedSavings > 0 ? "text-success" : "text-muted-foreground"
                            )}
                          >
                            {g.capturedSavings > 0 ? formatCurrency(g.capturedSavings) : "—"}
                          </span>
                          {g.discountEligible > 0 && (
                            <div className="text-[10px] text-muted-foreground">
                              {(() => {
                                const pct = (g.capturedSavings / g.discountEligible) * 100;
                                return pct > 0 && pct < 0.5 ? "<0.5" : pct.toFixed(1);
                              })()}
                              % captured
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div>
                          <span
                            className={cn(
                              "text-xs font-semibold",
                              g.potentialOpportunity > 0 ? "text-primary" : "text-muted-foreground"
                            )}
                          >
                            {g.potentialOpportunity > 0
                              ? formatCurrency(g.potentialOpportunity)
                              : "—"}
                          </span>
                          {g.potentialOpportunity > 0 && g.capturedSavings >= 0 && (
                            <div className="text-xs text-amber-600">
                              {formatCurrency(g.potentialOpportunity - g.capturedSavings)}{" "}
                              uncaptured
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {simRate > 0 ? (
                          <div>
                            <span className="text-xs font-semibold text-foreground">
                              {formatCurrency(g.simulationSavings ?? 0)}
                            </span>
                            <div className="text-xs text-muted-foreground">
                              @ {simRate.toFixed(1)}%
                            </div>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>

                    {/* Expanded invoice rows */}
                    {isExpanded &&
                      sortedInvs.map((r, j) => {
                        const daysVal = computeDaysEarlyLate(r);
                        const isCaptured =
                          r.paid_date &&
                          r.early_pay_due_date &&
                          r.paid_date <= r.early_pay_due_date;
                        const invoiceSimSavings =
                          simRate > 0 ? (r.amount_invoiced * simRate) / 100 : 0;
                        return (
                          <TableRow
                            key={`${g.vendor_name}-${j}`}
                            className="bg-muted/20 border-l-2 border-l-border/40 [&_td]:py-0.5"
                          >
                            <TableCell></TableCell>
                            <TableCell>
                              <div className="pl-4">
                                <span className="text-xs text-muted-foreground font-mono">
                                  {r.invoice_number || r.description || "—"}
                                </span>
                                <div className="text-xs text-muted-foreground/70 whitespace-nowrap">
                                  {fmtDate(r.invoiced_date)}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                Due {fmtDate(r.due_date)}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              {r.early_pay_due_date ? (
                                <span className="text-xs text-primary whitespace-nowrap">
                                  {fmtDate(r.early_pay_due_date)}
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="text-xs">{formatCurrency(r.amount_invoiced)}</span>
                            </TableCell>
                            <TableCell className="text-right">
                              {r.potential_savings > 0 ? (
                                <span className="text-xs text-primary">
                                  {formatCurrency(r.potential_savings)}
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {r.discount_pct != null && r.discount_pct > 0 ? (
                                <span className="text-xs text-primary">
                                  {r.discount_pct.toFixed(1)}%
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              {r.terms ? (
                                (() => {
                                  const hasDiscount = lookupTermsCode(r.terms)?.discountPct != null;
                                  return (
                                    <span
                                      className={cn(
                                        "inline-block px-1 py-0 rounded text-xs font-medium border leading-tight",
                                        hasDiscount
                                          ? "bg-success/10 text-success border-success/20"
                                          : "bg-muted text-muted-foreground border-border"
                                      )}
                                    >
                                      {describeTermsCode(r.terms)}
                                    </span>
                                  );
                                })()
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <span className={cn("text-xs", daysEarlyLateColor(daysVal))}>
                                {formatDaysEarlyLate(daysVal)}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              {isCaptured ? (
                                <span className="text-xs text-success">
                                  {formatCurrency(r.potential_savings)}
                                </span>
                              ) : r.discount_pct != null && r.discount_pct > 0 ? (
                                <span className="text-xs text-amber-600">
                                  {formatCurrency(r.potential_savings)} uncaptured
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="text-xs text-muted-foreground">—</span>
                            </TableCell>
                            <TableCell className="text-right">
                              {simRate > 0 ? (
                                <span className="text-xs text-foreground">
                                  {formatCurrency(invoiceSimSavings)}
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
